import { useEffect, useState } from "react";
import { CalendarDays, Loader2, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackConversion } from "@/lib/analytics";
import { useContent } from "@/context/ContentContext";

const EMPTY = { name: "", email: "", phone: "", treatment: "", preferred_date: "", preferred_time: "", message: "" };

const TIME_SLOTS = [
  { id: "morning", label: "Morning", range: "10:00 AM – 12:00 PM" },
  { id: "afternoon", label: "Afternoon", range: "12:00 – 4:00 PM" },
  { id: "evening", label: "Evening", range: "4:00 – 8:00 PM" },
];

export const EnquiryForm = ({ initialTreatment = "", prefix = "enquiry", onSuccess }) => {
  const { content } = useContent();
  const treatments = content?.treatments || [];
  const s = content?.settings || {};
  const [form, setForm] = useState({ ...EMPTY, treatment: initialTreatment });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [slots, setSlots] = useState(null); // { full: {morning: bool,...}, capacity }

  useEffect(() => {
    setForm((f) => ({ ...f, treatment: initialTreatment }));
  }, [initialTreatment]);

  // fetch slot fullness whenever a day is chosen
  useEffect(() => {
    if (!form.preferred_date) {
      setSlots(null);
      return;
    }
    let alive = true;
    api.get(`/slots/availability?date=${form.preferred_date}`).then(({ data }) => {
      if (!alive) return;
      setSlots(data);
      if (data.full?.[form.preferred_time]) setForm((f) => ({ ...f, preferred_time: "" }));
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.preferred_date]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const waText = (d) =>
    encodeURIComponent(
      `Hello Royal Skin Cosmetic Clinic — I've just requested a consultation.\n\nName: ${d.name}\nTreatment: ${d.treatment || "Not sure yet"}\nPreferred: ${d.preferred_date || "Flexible"}${d.preferred_time ? ` · ${d.preferred_time}` : ""}\nPhone: ${d.phone}`
    );

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // link the visitor's push subscription so the clinic can send a reminder
      let push_endpoint = "";
      try {
        const reg = await navigator.serviceWorker?.getRegistration();
        const sub = await reg?.pushManager?.getSubscription();
        push_endpoint = sub?.endpoint || "";
      } catch {}
      await api.post("/enquiries", { ...form, push_endpoint });
      trackConversion({ treatment: form.treatment || "General consultation" });
      setDone({ ...form });
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your enquiry. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const when = done.preferred_date
      ? `${format(new Date(done.preferred_date + "T00:00:00"), "EEEE, d MMMM yyyy")}${done.preferred_time ? ` · ${done.preferred_time}` : ""}`
      : "Flexible timing";
    return (
      <div className="rounded-[4px] border border-[#D4AF37]/35 bg-[#FFFDF9] p-7 text-center" data-testid={`${prefix}-success-card`}>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#D4AF37]/15">
          <MessageCircle className="h-5 w-5 text-[#B38B2D]" aria-hidden="true" />
        </div>
        <h3 className="mt-4 font-serif text-2xl font-semibold text-[#1A1615]">Request received, {done.name.split(" ")[0]}</h3>
        <p className="mt-2 text-sm font-light leading-relaxed text-[#5A504B]">
          {done.treatment || "Consultation"} · {when}
          <br />
          Our concierge will confirm shortly — usually within the hour.
        </p>
        {s.whatsapp && (
          <a
            href={`https://wa.me/${s.whatsapp}?text=${waText(done)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${prefix}-wa-confirm`}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-[3px] bg-[#25D366] px-5 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:brightness-105 hover:shadow-[0_10px_30px_rgba(37,211,102,0.35)]"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" /> Get my confirmation on WhatsApp
          </a>
        )}
        <button
          type="button"
          onClick={() => {
            setDone(null);
            onSuccess?.();
          }}
          data-testid={`${prefix}-success-done`}
          className="mt-3 w-full px-5 py-3 text-xs font-medium uppercase tracking-[0.15em] text-[#8A7364] transition-colors hover:text-[#1A1615]"
        >
          {onSuccess ? "Done" : "Make another request"}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${prefix}-form`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${prefix}-name`} className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
            Full Name
          </label>
          <input id={`${prefix}-name`} required value={form.name} onChange={set("name")} placeholder="Your name" data-testid={`${prefix}-name-input`} className="input-lux" />
        </div>
        <div>
          <label htmlFor={`${prefix}-phone`} className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
            Phone
          </label>
          <input id={`${prefix}-phone`} required type="tel" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" data-testid={`${prefix}-phone-input`} className="input-lux" />
        </div>
      </div>
      <div>
        <label htmlFor={`${prefix}-email`} className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
          Email
        </label>
        <input id={`${prefix}-email`} required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid={`${prefix}-email-input`} className="input-lux" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${prefix}-treatment`} className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
            Treatment
          </label>
          <select id={`${prefix}-treatment`} value={form.treatment} onChange={set("treatment")} data-testid={`${prefix}-treatment-select`} className="input-lux">
            <option value="">Not sure yet — advise me</option>
            {treatments.map((t) => (
              <option key={t.id} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={`${prefix}-date`} className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
            Preferred Date
          </label>
          {/* Native date picker under the styled button — opens the phone's own
              calendar on every device (the popover calendar failed on some) */}
          <div className="relative">
            <input
              type="date"
              id={`${prefix}-date`}
              min={format(new Date(), "yyyy-MM-dd")}
              value={form.preferred_date}
              onChange={(e) => setForm((f) => ({ ...f, preferred_date: e.target.value }))}
              data-testid={`${prefix}-date-input`}
              aria-label="Preferred date"
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
            />
            <div className="input-lux pointer-events-none flex w-full items-center justify-between text-left" aria-hidden="true">
              <span className={form.preferred_date ? "text-[#1A1A1A]" : "text-[#A39288]"}>
                {form.preferred_date ? format(new Date(form.preferred_date + "T00:00:00"), "EEEE, d MMMM yyyy") : "Choose your day"}
              </span>
              <CalendarDays className="h-4 w-4 shrink-0 text-[#B38B2D]" aria-hidden="true" />
            </div>
          </div>
          {form.preferred_date && (
            <div className="mt-3" data-testid={`${prefix}-time-slots`}>
              <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.15em] text-[#8A7364]">Preferred time</p>
              <div className="grid grid-cols-3 gap-2">
                {TIME_SLOTS.map((slot) => {
                  const active = form.preferred_time === slot.id;
                  const full = !!slots?.full?.[slot.id];
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      disabled={full}
                      onClick={() => setForm((f) => ({ ...f, preferred_time: active ? "" : slot.id }))}
                      data-testid={`${prefix}-slot-${slot.id}`}
                      aria-pressed={active}
                      aria-disabled={full}
                      className={`rounded-[3px] border px-2 py-2.5 text-center transition-all duration-300 ease-liquid focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                        full
                          ? "cursor-not-allowed border-[#1A1A1A]/5 bg-[#1A1A1A]/[0.04] opacity-50"
                          : active
                            ? "border-[#D4AF37] bg-[#D4AF37]/15 shadow-[0_6px_18px_rgba(212,175,55,0.2)]"
                            : "border-[#1A1A1A]/10 bg-white/60 hover:border-[#D4AF37]/50"
                      }`}
                    >
                      <span className={`block text-xs font-semibold ${active ? "text-[#8a6b12]" : "text-[#1A1A1A]"}`}>
                        {slot.label}
                        {full && <span className="ml-1 font-normal text-[#A39288]">· Full</span>}
                      </span>
                      <span className="mt-0.5 block text-[9px] font-light text-[#8A7364]">{slot.range}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <div>
        <label htmlFor={`${prefix}-message`} className="mb-1.5 block text-xs font-medium uppercase tracking-[0.15em] text-[#5A504B]">
          Notes <span className="normal-case tracking-normal text-[#A39288]">(optional)</span>
        </label>
        <textarea id={`${prefix}-message`} rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about your skin goals..." data-testid={`${prefix}-message-input`} className="input-lux resize-none" />
      </div>
      <button type="submit" disabled={submitting} data-testid={`${prefix}-submit-button`} className="btn-gold w-full px-6 py-3.5 text-sm disabled:opacity-60">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        {submitting ? "Sending..." : "Request My Consultation"}
      </button>
      {s.whatsapp && (
        <a
          href={`https://wa.me/${s.whatsapp}?text=${encodeURIComponent("Hello Royal Skin Cosmetic Clinic, I would like to book a consultation.")}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${prefix}-whatsapp-link`}
          className="flex items-center justify-center gap-2 text-xs font-medium text-[#5A504B] transition-colors hover:text-[#25D366]"
        >
          <MessageCircle className="h-4 w-4" aria-hidden="true" /> Prefer WhatsApp? Chat with us instantly
        </a>
      )}
    </form>
  );
};
